import { cn } from '@/lib/utils';

/**
 * Indeterminate "scan bar" — a blue gradient segment that sweeps across the
 * track. Use for full-page or route-level loading where there's no measurable
 * progress. Reads as the system scanning/working rather than a generic spinner.
 *
 * Render `inline` for a thin bar inside a panel, or default (fixed) to pin it
 * to the very top of the viewport like a navigation progress bar.
 */
export function ScanBar({
  className,
  inline = false,
}: {
  className?: string;
  inline?: boolean;
}) {
  return (
    <div
      role="progressbar"
      aria-busy="true"
      aria-label="Loading"
      className={cn(
        'kibo-scanbar',
        inline ? 'rounded-full' : 'fixed inset-x-0 top-0 z-[100]',
        className,
      )}
    />
  );
}

/**
 * Centered full-page loader: branded scan bar over a short status line.
 * Drop-in replacement for spinner-based page fallbacks.
 */
export function PageLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background">
      <div className="w-56">
        <ScanBar inline />
      </div>
      <p className="text-sm font-medium tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}
